import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

// Common stop words to filter out
const STOP_WORDS = new Set([
  'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'i', 'it', 'for',
  'not', 'on', 'with', 'he', 'as', 'you', 'do', 'at', 'this', 'but', 'his',
  'by', 'from', 'they', 'we', 'say', 'her', 'she', 'or', 'an', 'will', 'my',
  'one', 'all', 'would', 'there', 'their', 'what', 'so', 'up', 'out', 'if',
  'about', 'who', 'get', 'which', 'go', 'me', 'when', 'make', 'can', 'like',
  'time', 'no', 'just', 'him', 'know', 'take', 'people', 'into', 'year', 'your',
  'good', 'some', 'could', 'them', 'see', 'other', 'than', 'then', 'now', 'look',
  'only', 'come', 'its', 'over', 'think', 'also', 'back', 'after', 'use', 'two',
  'how', 'our', 'way', 'even', 'new', 'want', 'because', 'any', 'these', 'give',
  'day', 'most', 'us', 'is', 'are', 'was', 'were', 'been', 'has', 'had', 'did',
  'does', 'very', 'much', 'more', 'well', 'should', "don't", "i'm", "it's",
  "that's", "we're", "they're", "can't", "won't", "isn't", "aren't", "didn't",
]);

interface WordEntry {
  word: string;
  count: number;
  size: number;
}

const WordCloudWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const containerRef = useRef<HTMLDivElement>(null);
  const [, setDimensions] = useState({ width: 300, height: 200 });

  // Extract word frequencies
  const words: WordEntry[] = useMemo(() => {
    const transcript = state?.transcript ?? [];
    const freq = new Map<string, number>();

    for (const entry of transcript) {
      const tokens = entry.text
        .toLowerCase()
        .replace(/[^a-z\s'-]/g, '')
        .split(/\s+/)
        .filter(w => w.length > 2 && !STOP_WORDS.has(w));

      for (const token of tokens) {
        freq.set(token, (freq.get(token) || 0) + 1);
      }
    }

    // Sort by frequency, take top 40
    const sorted = Array.from(freq.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 40);

    if (sorted.length === 0) return [];

    const maxCount = sorted[0][1];
    const minCount = sorted[sorted.length - 1][1];
    const range = maxCount - minCount || 1;

    return sorted.map(([word, count]) => ({
      word,
      count,
      size: 12 + ((count - minCount) / range) * 24, // 12px to 36px
    }));
  }, [state?.transcript]);

  // Track container size
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!state) return null;

  if (words.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        Waiting for transcript...
      </div>
    );
  }

  // Simple scatter layout
  const tealShades = [
    'rgba(0,199,196,0.95)',
    'rgba(0,199,196,0.75)',
    'rgba(0,199,196,0.55)',
    'rgba(0,199,196,0.40)',
  ];

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden p-2">
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 h-full content-start pt-4">
        {words.map((entry, i) => {
          let fontWeight: number;
          if (i < 5) fontWeight = 700;
          else if (i < 15) fontWeight = 600;
          else fontWeight = 400;

          return (
          <span
            key={entry.word}
            className="inline-block transition-all duration-700 hover:scale-110"
            style={{
              fontSize: `${entry.size}px`,
              color: tealShades[Math.min(3, Math.floor(i / 10))],
              fontWeight,
              opacity: 0.5 + (entry.count / (words[0]?.count || 1)) * 0.5,
            }}
            title={`${entry.word}: ${entry.count}`}
          >
            {entry.word}
          </span>
          );
        })}
      </div>
    </div>
  );
};

export default WordCloudWidget;
